;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { formatNumber } from '@qogita/format/number'
import { pluralize } from '@qogita/format/string'
import { cn } from '@qogita/ui/utils/cn'
import { BaseAnchor } from '@qogita/ui/v2/base-anchor'
import { BaseButton } from '@qogita/ui/v2/base-button'
import { Icon } from '@qogita/ui/v2/icon'
import { cva, VariantProps } from 'class-variance-authority'
import { usePathname, useSearchParams } from 'next/navigation'
import { ComponentProps, ComponentType } from 'react'

type CustomLinkProps = ComponentProps<typeof BaseAnchor> & {
  className?: string
  'aria-label'?: string
  'aria-current'?: 'page' | undefined
  children: React.ReactNode
}

export function PaginationRebranded({
  currentPage,
  resultsCount,
  pageSize,
  resultsLabel = 'result',
  LinkComponent = BaseAnchor,
  scroll = true,
  rebranded = false,
  className,
}: {
  currentPage: number
  resultsCount: number
  pageSize: number
  resultsLabel?: string
  LinkComponent?: ComponentType<CustomLinkProps>
  scroll?: boolean
  rebranded?: VariantProps<typeof paginationItemStyles>['rebranded']
  className?: string
}) {
  const pluralizedLabel = pluralize({
    word: resultsLabel,
    count: resultsCount,
  })
  const totalPages = Math.ceil(resultsCount / pageSize)
  const pages = getPageNumbers(currentPage, totalPages)

  if (resultsCount === 0) {
    // If the result set is empty don't show pagination at all
    return null
  }

  const hasMultiplePages = totalPages > 1

  return (
    <nav className={cn('@container', className)} aria-label="Pagination">
      <div className="flex flex-row flex-wrap items-center justify-between gap-4">
        <PaginationSummary
          rebranded={rebranded}
          currentPage={currentPage}
          pageSize={pageSize}
          totalResults={resultsCount}
          resultsLabel={pluralizedLabel}
        />
        {hasMultiplePages ? (
          <PaginationControls
            rebranded={rebranded}
            pages={pages}
            LinkComponent={LinkComponent}
            scroll={scroll}
          />
        ) : null}
      </div>
    </nav>
  )
}

function PaginationControls({
  pages,
  LinkComponent,
  scroll,
  rebranded,
}: {
  pages: PageNumber[]
  LinkComponent: ComponentType<CustomLinkProps>
  scroll: boolean
  rebranded?: VariantProps<typeof paginationItemStyles>['rebranded']
}) {
  const currentPage = pages.find((page) => page.isCurrent)?.number ?? 1
  const totalPages = pages.at(-1)?.number ?? 1

  return (
    <div className="isolate inline-flex -space-x-px rounded-md shadow-xs">
      <PaginationPrevious
        page={currentPage}
        LinkComponent={LinkComponent}
        scroll={scroll}
        rebranded={rebranded}
      />
      {pages.map((page, index) =>
        page.number === -1 ? (
          <PaginationEllipsis
            key={`ellipsis-${index}`}
            className="hidden @sm:inline-flex"
            rebranded
          />
        ) : (
          <PaginationPage
            rebranded={rebranded}
            key={`page-${index}-${page.number}`}
            number={page.number}
            isCurrent={page.isCurrent}
            LinkComponent={LinkComponent}
            className="hidden @sm:inline-flex"
            scroll={scroll}
          />
        ),
      )}
      <PaginationNext
        page={currentPage}
        totalPages={totalPages}
        LinkComponent={LinkComponent}
        scroll={scroll}
        rebranded={rebranded}
      />
    </div>
  )
}

const paginationItemStyles = cva(
  cn(
    // Layout & spacing
    'relative inline-flex h-9.5 w-9.5 items-center justify-center border',
    'first:rounded-l-md first:px-2 last:rounded-r-md last:px-2',

    'aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-white',
  ),
  {
    variants: {
      rebranded: {
        false: [
          'text-sm font-medium',
          // Colors
          'border-gray-300 text-gray-500',
          // Interactive states
          'hover:bg-gray-200',
          'focus-visible:z-20',
          // Current page styles
          'aria-[current=page]:border-primary-500 aria-[current=page]:bg-primary-50 aria-[current=page]:text-primary-600 aria-[current=page]:z-10',
        ],
        true: [
          'text-xs font-light',
          // Colors
          'border-gray-300 text-gray-500',
          // Interactive states
          'hover:bg-gray-200',
          'focus-visible:z-20',
          // Current page styles
          'aria-[current=page]:z-10 aria-[current=page]:border-gray-900 aria-[current=page]:bg-gray-800 aria-[current=page]:text-gray-100',
        ],
      },
    },
    defaultVariants: {
      rebranded: false,
    },
  },
)

function usePaginationHref(page: number | null) {
  const searchParams = useSearchParams()
  const pathname = usePathname() ?? ''
  const newSearchParams = new URLSearchParams(searchParams ?? undefined)

  if (page === null) {
    return ''
  }

  if (page === 1) {
    newSearchParams.delete('page')
  } else {
    newSearchParams.set('page', String(page))
  }

  return newSearchParams.size === 0
    ? pathname
    : `${pathname}?${newSearchParams}`
}

function PaginationPrevious({
  page,
  LinkComponent,
  scroll,
  rebranded,
}: {
  page: number
  LinkComponent: ComponentType<CustomLinkProps>
  scroll: boolean
  rebranded?: VariantProps<typeof paginationItemStyles>['rebranded']
}) {
  const isFirstPage = page <= 1
  const previousPage = Math.max(1, page - 1)
  const href = usePaginationHref(isFirstPage ? null : previousPage)

  if (isFirstPage) {
    return (
      <BaseButton
        className={paginationItemStyles({ rebranded })}
        aria-disabled="true"
        aria-label="Previous page"
      >
        <Icon name="heroicons/solid/chevron-left" className="size-4" />
      </BaseButton>
    )
  }

  return (
    <LinkComponent
      href={href}
      className={paginationItemStyles({ rebranded })}
      aria-label="Previous page"
      scroll={scroll}
    >
      <Icon name="heroicons/solid/chevron-left" className="size-4" />
    </LinkComponent>
  )
}

function PaginationPage({
  number,
  isCurrent,
  LinkComponent,
  className,
  scroll,
  rebranded,
}: {
  number: number
  isCurrent: boolean
  LinkComponent: ComponentType<CustomLinkProps>
  className?: string
  scroll: boolean
  rebranded?: VariantProps<typeof paginationItemStyles>['rebranded']
}) {
  const href = usePaginationHref(number)

  return (
    <LinkComponent
      href={href}
      className={cn(paginationItemStyles({ rebranded }), className)}
      aria-current={isCurrent ? 'page' : undefined}
      scroll={scroll}
    >
      {number}
    </LinkComponent>
  )
}

function PaginationEllipsis({
  className,
  rebranded,
}: {
  className?: string
  rebranded?: VariantProps<typeof paginationItemStyles>['rebranded']
}) {
  return (
    <span
      className={cn(
        paginationItemStyles({ rebranded }),
        'hover:bg-transparent',
        className,
      )}
    >
      &hellip;
    </span>
  )
}

function PaginationNext({
  page,
  totalPages,
  LinkComponent,
  scroll,
  rebranded,
}: {
  page: number
  totalPages: number
  LinkComponent: ComponentType<CustomLinkProps>
  scroll: boolean
  rebranded?: VariantProps<typeof paginationItemStyles>['rebranded']
}) {
  const isLastPage = page >= totalPages
  const nextPage = Math.min(totalPages, page + 1)
  const href = usePaginationHref(isLastPage ? null : nextPage)

  if (isLastPage) {
    return (
      <BaseButton
        className={paginationItemStyles({ rebranded })}
        aria-disabled="true"
        aria-label="Next page"
      >
        <Icon name="heroicons/solid/chevron-right" className="size-4" />
      </BaseButton>
    )
  }

  return (
    <LinkComponent
      href={href}
      className={paginationItemStyles({ rebranded })}
      aria-label="Next page"
      scroll={scroll}
    >
      <Icon name="heroicons/solid/chevron-right" className="size-4" />
    </LinkComponent>
  )
}

const paginationSummaryStyles = cva('', {
  variants: {
    rebranded: {
      true: 'text-base font-light text-gray-700 [&>span]:font-light',
      false: 'text-sm text-gray-700 [&>span]:font-medium',
    },
  },
})

function PaginationSummary({
  currentPage,
  pageSize,
  totalResults,
  resultsLabel,
  rebranded = false,
}: {
  currentPage: number
  pageSize: number
  totalResults: number
  resultsLabel: string
  rebranded: VariantProps<typeof paginationSummaryStyles>['rebranded']
}) {
  if (totalResults === 0) return null

  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalResults)

  return (
    <p className={paginationSummaryStyles({ rebranded })}>
      Showing <span>{formatNumber({ value: startItem })}</span> to{' '}
      <span>{formatNumber({ value: endItem })}</span> of{' '}
      <span>{resultsLabel}</span>
    </p>
  )
}

/**
 * Represents a page number in the pagination UI
 * -1 represents an ellipsis
 */
type PageNumber = {
  number: number
  isCurrent: boolean
}

/**
 * Generates an array of page numbers to display in the pagination UI
 * Always shows first and last pages
 * Shows ellipsis when there's a gap of at least 2 numbers
 * Shows 1 page on each side of current page
 * For small total pages (≤ 5), shows all pages
 *
 * Examples:
 * - Page 1 of 5: [1, 2, 3, 4, 5]
 * - Page 2 of 5: [1, 2, 3, 4, 5]
 * - Page 3 of 5: [1, 2, 3, 4, 5]
 * - Page 1 of 10: [1, 2, 3, ..., 10]
 * - Page 5 of 10: [1, ..., 4, 5, 6, ..., 10]
 * - Page 8 of 10: [1, ..., 7, 8, 9, 10]
 * - Page 10 of 10: [1, ..., 8, 9, 10]
 */
function getPageNumbers(currentPage: number, totalPages: number): PageNumber[] {
  // For 5 or fewer pages, show all pages
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => ({
      number: index + 1,
      isCurrent: index + 1 === currentPage,
    }))
  }

  const pages: PageNumber[] = []

  // Always add first page if we're not on it
  if (currentPage !== 1) {
    pages.push({ number: 1, isCurrent: false })
  }

  // If we're not near the start, add first ellipsis
  if (currentPage > 3) {
    pages.push({ number: -1, isCurrent: false })
  }

  // Add page before current if we're not at the start
  if (currentPage > 2) {
    pages.push({ number: currentPage - 1, isCurrent: false })
  }

  // Add current page
  pages.push({ number: currentPage, isCurrent: true })

  // Add page after current if we're not at the end
  if (currentPage < totalPages - 1) {
    pages.push({ number: currentPage + 1, isCurrent: false })
  }

  // If we're not near the end, add last ellipsis
  if (currentPage < totalPages - 2) {
    pages.push({ number: -1, isCurrent: false })
  }

  // Always add last page if we're not on it and it's not already added
  if (currentPage !== totalPages && pages.at(-1)?.number !== totalPages) {
    pages.push({ number: totalPages, isCurrent: false })
  }

  return pages
}
